import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';

import './ServicesSlider.css';

const ServicesSlider = () => {
  const [expandedService, setExpandedService] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Updated services array with new content
  const servicesData = [
  // Real Estate & Property Development
  {
    id: 1,
    category: 'Real Estate & Property Development',
    title: 'Commercial Real Estate Development Funds',
    image: '/assets/images/development-funds.jpg',
    shortDesc: 'Sourcing funding for infrastructure, social, and commercial real estate development projects.',
    fullDesc: 'Our team specializes in sourcing funding for infrastructure, social, and economic development projects. We support businesses, governments, and organizations in accessing sustainable financing and structuring long-term funding solutions for both public and private sector development initiatives.',
    keywords: ['real estate development financing', 'commercial property development', 'infrastructure finance']
  },
  {
    id: 2,
    category: 'Real Estate & Property Development',
    title: 'Hotel & Hospitality Construction Financing',
    image: '/assets/images/hotel-construction.jpg',
    shortDesc: 'Specialized financing for hotel construction, resort development, and hospitality infrastructure projects.',
    fullDesc: 'We provide comprehensive financing solutions for hotel and hospitality projects, from boutique hotels to large resort developments. Our team works with international lenders experienced in hospitality real estate to structure optimal financing packages that support your vision from groundbreaking through grand opening.',
    keywords: ['hotel construction lenders', 'hospitality financing', 'resort development']
  },
  {
    id: 3,
    category: 'Real Estate & Property Development',
    title: 'Apartment & Residential Construction Financing',
    image: '/assets/images/development.webp',
    shortDesc: 'Funding solutions for multi-family residential developments, apartment complexes, and housing projects.',
    fullDesc: 'Our residential construction financing supports developers building apartment complexes, condominiums, and affordable housing projects. We facilitate access to construction loans, bridge financing, and permanent financing options tailored to the unique needs of residential real estate developers.',
    keywords: ['apartment construction financing', 'residential development', 'multi-family financing']
  },
  {
    id: 4,
    category: 'Real Estate & Property Development',
    title: 'Commercial Building Financing',
    image: '/assets/images/commercial-building.jpg',
    shortDesc: 'Financing for office buildings, retail centers, warehouses, and mixed-use commercial properties.',
    fullDesc: 'We arrange financing for all types of commercial building projects including office towers, retail shopping centers, industrial warehouses, and mixed-use developments. Our network of commercial property lenders provides competitive rates and flexible terms for both construction and permanent financing.',
    keywords: ['commercial building financing', 'commercial property lender', 'office building finance']
  },

  // Business & Corporate Finance
  {
    id: 5,
    category: 'Business & Corporate Finance',
    title: 'Secured Business Loans',
    image: '/assets/images/loans.png',
    shortDesc: 'Capital to expand your business, refinance operations, or pursue new opportunities secured by collateral.',
    fullDesc: 'We facilitate access to secured business loans that provide the capital needed for business expansion, equipment purchases, working capital, or debt refinancing. Our solutions allow you to leverage your business assets, real estate, or other collateral to secure favorable loan terms and competitive interest rates.',
    keywords: ['secured business loans', 'business capital', 'company funding']
  },
  {
    id: 6,
    category: 'Business & Corporate Finance',
    title: 'Acquisition & Merger Financing',
    image: '/assets/images/acquisition-finance.webp',
    shortDesc: 'Strategic financing solutions for business acquisitions, mergers, and corporate restructuring.',
    fullDesc: 'Our acquisition financing services support companies looking to acquire other businesses, merge operations, or restructure corporate entities. We structure debt and equity financing packages that align with your strategic objectives while optimizing your capital structure and minimizing dilution.',
    keywords: ['acquisition financing', 'merger finance', 'corporate restructuring']
  },
  {
    id: 7,
    category: 'Business & Corporate Finance',
    title: 'Corporate Finance Solutions',
    image: '/assets/images/corporate-finance.webp',
    shortDesc: 'Comprehensive debt, equity, and blended financing solutions for corporate growth and expansion.',
    fullDesc: 'We provide sophisticated corporate finance solutions including debt financing, equity investments, mezzanine funding, and blended financing structures. Our team works with corporations of all sizes to access the capital needed for expansion, refinancing, or strategic initiatives through our global network of institutional investors and lenders.',
    keywords: ['corporate finance', 'debt financing', 'equity financing', 'blended financing']
  },

  // Trade & Working Capital
  {
    id: 8,
    category: 'Trade & Working Capital',
    title: 'Trade Finance Solutions',
    image: '/assets/images/trade-finance.jpg',
    shortDesc: 'Comprehensive trade finance instruments to facilitate international and domestic commerce.',
    fullDesc: 'Our trade finance services include letters of credit, bank guarantees, documentary collections, and other trade finance instruments that reduce risk and improve cash flow for importers and exporters. We work with international banks to provide reliable trade finance solutions that keep your supply chain moving smoothly.',
    keywords: ['trade finance', 'trade financing', 'trade finance instruments', 'international trade']
  },
  {
    id: 9,
    category: 'Trade & Working Capital',
    title: 'Invoice Financing & Factoring',
    image: '/assets/images/invoice-financing.webp',
    shortDesc: 'Unlock cash tied up in unpaid invoices through invoice financing, factoring, and discounting.',
    fullDesc: 'Convert your accounts receivable into immediate working capital with our invoice financing and factoring services. We provide invoice funding, invoice discounting, and full factoring solutions that improve cash flow, reduce credit risk, and allow you to focus on growing your business rather than chasing payments.',
    keywords: ['invoice financing', 'invoice funding', 'factoring finance', 'invoice discounting']
  },
  {
    id: 10,
    category: 'Trade & Working Capital',
    title: 'Purchase Order Financing',
    image: '/assets/images/po-financing.jpg',
    shortDesc: 'Finance large orders and fulfill customer demands without straining your working capital.',
    fullDesc: 'Our purchase order financing solutions enable businesses to accept and fulfill large orders they otherwise couldn\'t afford to take on. We advance funds to pay your suppliers directly, allowing you to complete the order and deliver to your customer without depleting your working capital or turning down profitable opportunities.',
    keywords: ['PO financing', 'purchase order finance', 'order fulfillment funding']
  },
  {
    id: 11,
    category: 'Trade & Working Capital',
    title: 'Working Capital Solutions',
    image: '/assets/images/working-capital.webp',
    shortDesc: 'Flexible working capital financing to manage cash flow, inventory, and operational expenses.',
    fullDesc: 'We provide working capital solutions that give your business the financial flexibility to manage day-to-day operations, purchase inventory, meet payroll, and seize growth opportunities. Our revolving credit facilities and working capital loans are designed to adapt to your business cycles and seasonal fluctuations.',
    keywords: ['working capital', 'cash flow financing', 'operational funding']
  },

  // Equipment & Asset Finance
  {
    id: 12,
    category: 'Equipment & Asset Finance',
    title: 'Equipment Loans & Leasing',
    image: '/assets/images/equipment-loans.jpg',
    shortDesc: 'Finance the equipment your business needs, from heavy machinery to office technology.',
    fullDesc: 'We finance general-purpose equipment such as packaging machines and office equipment, heavy-industrial equipment including manufacturing lines, printing presses and stationary machinery, construction equipment, and commercial vehicles. Our equipment financing and leasing options preserve your working capital while ensuring you have the tools to operate efficiently.',
    keywords: ['equipment financing', 'equipment leasing', 'machinery funding']
  },
  {
    id: 13,
    category: 'Equipment & Asset Finance',
    title: 'Restaurant & Franchise Equipment Financing',
    image: '/assets/images/restaurant-financing.jpg',
    shortDesc: 'Specialized financing for restaurant equipment, kitchen facilities, and franchise development.',
    fullDesc: 'Our restaurant and franchise financing programs help food service businesses acquire commercial kitchen equipment, dining furniture, point-of-sale systems, and complete facility buildouts. We understand the unique needs of the hospitality industry and provide flexible financing that supports both independent restaurants and franchise operations.',
    keywords: ['restaurant financing', 'franchise financing', 'commercial kitchen equipment']
  },

  // Construction & Contractor Finance
  {
    id: 14,
    category: 'Construction & Contractor Finance',
    title: 'Construction Project Financing',
    image: '/assets/images/construction-financing.png',
    shortDesc: 'Comprehensive financing solutions for construction projects of all sizes and complexities.',
    fullDesc: 'We provide construction financing for projects ranging from small commercial builds to large infrastructure developments. Our construction loans cover land acquisition, pre-development costs, construction expenses, and permanent financing. We work with developers and contractors to structure financing that matches project timelines and cash flow requirements.',
    keywords: ['construction financing', 'construction funds', 'building finance', 'project financing']
  },
  {
    id: 15,
    category: 'Construction & Contractor Finance',
    title: 'Contractor Working Capital & Bonding',
    image: '/assets/images/contractor-financing.png',
    shortDesc: 'Financial solutions for contractors including working capital, equipment finance, and bonding support.',
    fullDesc: 'Our contractor financing services provide the working capital, equipment financing, and bonding capacity that construction companies need to bid on and complete projects. We help contractors manage cash flow between project milestones, finance equipment purchases, and secure performance bonds for larger contracts.',
    keywords: ['contractor financing', 'construction company funding', 'contractor working capital']
  },

  // Green Energy & Sustainability
  {
    id: 16,
    category: 'Green Energy & Sustainability',
    title: 'Solar & Renewable Energy Project Financing',
    image: '/assets/images/solar-financing.jpg',
    shortDesc: 'Specialized financing for solar installations, wind farms, and renewable energy infrastructure.',
    fullDesc: 'We arrange financing for renewable energy projects including solar farms, rooftop solar installations, wind energy facilities, and other clean energy infrastructure. Our team connects project developers with green energy investors and lenders who understand the technical and financial aspects of renewable energy, including tax incentives and power purchase agreements.',
    keywords: ['solar project financing', 'renewable energy projects', 'green energy finance']
  },

  // Agriculture & Land
  {
    id: 17,
    category: 'Agriculture & Land Finance',
    title: 'Agricultural & Farmland Financing',
    image: '/assets/images/agriculture-funding.webp',
    shortDesc: 'Financing solutions for farmland acquisition, agricultural operations, and agribusiness development.',
    fullDesc: 'Our agricultural financing services support farmers, ranchers, and agribusiness operations with loans for land purchases, equipment acquisition, operating expenses, and facility construction. We understand the seasonal nature of agriculture and structure financing that aligns with harvest cycles and commodity markets.',
    keywords: ['agriculture funding', 'farmland financing', 'agribusiness finance']
  },

  // Investment & Joint Ventures
  {
    id: 18,
    category: 'Investment & Joint Ventures',
    title: 'International Investor Connections',
    image: '/assets/images/international-investors.webp',
    shortDesc: 'Connect with venture capital, angel investors, and institutional investors worldwide.',
    fullDesc: 'We facilitate connections between project developers and international investors including venture capital funds, angel investor networks, and institutional investors from China, Japan, Saudi Arabia, Europe, and the United States. Our global network helps businesses access equity capital, strategic partnerships, and growth funding from investors aligned with their industry and vision.',
    keywords: ['venture capital', 'angel investors', 'international investors', 'project investors']
  },
  {
    id: 19,
    category: 'Investment & Joint Ventures',
    title: 'Joint Venture & Partnership Structuring',
    image: '/assets/images/joint-venture.webp',
    shortDesc: 'Structure and finance joint venture partnerships for real estate, infrastructure, and business development.',
    fullDesc: 'Our joint venture services help businesses structure strategic partnerships that combine expertise, resources, and capital. We assist with joint venture formation, financing arrangements, and partnership agreements for real estate development, infrastructure projects, and business expansion initiatives across Africa, Europe, and international markets.',
    keywords: ['joint venture finance', 'partnership structuring', 'strategic alliances']
  },

  // Development Finance
  {
    id: 20,
    category: 'Development Finance',
    title: 'Community & Impact Investment',
    image: '/assets/images/impact-investment.webp',
    shortDesc: 'Low-interest loans and impact investment for community development and social enterprises.',
    fullDesc: 'We facilitate access to community development loans and partner with impact investors to support grassroots projects. Our microfinance and impact investment solutions are designed to empower small businesses and entrepreneurs in underserved communities, fostering economic growth, job creation, and social impact.',
    keywords: ['impact investing', 'community development', 'microfinance', 'social enterprise']
  },
  {
    id: 21,
    category: 'Development Finance',
    title: 'Infrastructure & PPP Project Finance',
    image: '/assets/images/infrastructure-finance.png',
    shortDesc: 'Financing for large-scale infrastructure, public-private partnerships, and development projects.',
    fullDesc: 'We specialize in structuring finance for major infrastructure projects including roads, bridges, ports, utilities, and public facilities. Our expertise in public-private partnerships (PPP) and project finance enables governments and developers to undertake large-scale projects with international funding from development finance institutions and commercial lenders.',
    keywords: ['infrastructure finance', 'PPP project finance', 'development finance institutions']
  }
];

  const toggleServiceDetails = (id) => {
    if (expandedService === id) {
      setExpandedService(null);
    } else {
      setExpandedService(id);
    }
  };

  // Framer Motion variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8, 
        ease: "easeOut" 
      }
    }
  };

  return (
    <motion.section 
      id="services" 
      className="services-section"
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      <div className="services-container">
        <motion.div 
          className="services-header"
          variants={headerVariants}
        >
          <h2 className="services-title">Global financial agency Services</h2>
          <p className="services-subtitle">
            Our core expertise lies in identifying optimal financing solutions, facilitating access to capital, 
            and streamlining the funding process through strategic partnerships and trusted networks.
          </p>
        </motion.div>

        <motion.div
          variants={headerVariants}
          className="services-slider-container"
        >
          <Swiper
            modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
            effect="coverflow"
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 2.5,
            }}
            spaceBetween={30}
            slidesPerView={1}
            centeredSlides={true} /* Center the slides */
            navigation
            pagination={{ clickable: true }}
            autoplay={{ 
              delay: 5000, 
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }}
            loop={true}
            loopFillGroupWithBlank={true}
            speed={800}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="services-swiper"
          >
            {servicesData.map((service) => (
              <SwiperSlide key={service.id}>
                <motion.div 
                  className="service-card"
                  whileHover={{ 
                    y: -10,
                    boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)"
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="service-icon">{service.icon}</div>
                  <div 
                    className="service-image" 
                    style={{ backgroundImage: `url(${service.image})` }}
                  ></div>
                  
                  <div className="service-content">
                    <h3 className="service-title">{service.title}</h3>
                    
                    <AnimatePresence mode="wait">
                      {expandedService === service.id ? (
                        <motion.p 
                          className="service-description"
                          key="full"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {service.fullDesc}
                        </motion.p>
                      ) : (
                        <motion.p 
                          className="service-description"
                          key="short"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {service.shortDesc}
                        </motion.p>
                      )}
                    </AnimatePresence>
                    
                    <motion.button
                      onClick={() => toggleServiceDetails(service.id)}
                      className="service-button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {expandedService === service.id ? 'Show Less' : 'Learn More'}
                    </motion.button>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
        
      </div>
    </motion.section>
  );
};

export default ServicesSlider;